// @flow

import Downshift from 'downshift';
import React, { type ComponentType, useState } from 'react';

import Menu from '../SelectField/Menu';
import SelectionBox from './SelectionBox';

import type { StateChangeOptions } from 'downshift';

// We can't safely assume that `Item` will always have a specific shape, because consumers that customize
// rendering might have items with additional/different properties flowing through.
type Item = *;

// User supplied functions that provides a key/label for an item. The item is guaranteed
// to be defined when passed to this function.
type GetKeyForItem = (item: Item) => string;
type GetLabelForItem = (item: Item) => string;

type Props = {
  /** a11y label, used if no labelId prop */
  ariaLabel?: string,
  /** browser autocomplete */
  autoComplete?: string,
  /** autofocus the input when rendered */
  autoFocus?: boolean,
  /**
   * Custom filter function to filter list of dropdown items matching inputValue.
   * If not supplied defaults to a simple 'startsWith' filter function (case insensitive).
   */
  filterItems: (
    inputValue: string,
    items: Array<Item>,
    getLabelForItem: GetLabelForItem
  ) => Array<Item>,
  /**
   * Callback that will be used to generate the react `key` to be used for each item on the menu list.
   * Defaults to a `value` property on each item object.
   */
  getKeyForItem: GetKeyForItem,
  /**
   * Callback that will be used to generate the string label to be rendered for each item on the menu list.
   * Defaults to a `label` property on each item object.
   */
  getLabelForItem: GetLabelForItem,
  /** Show error styling */
  hasError?: boolean,
  /** List of values that can be ignored when changed to */
  ignoredChanges?: Array<number>,
  /**
   * Optional prop for setting the initially selected item, but still letting `SearchableSelectField` manage
   * the selected item state for you.
   */
  initialSelectedItem?: Item,
  /** id of the input created by this select field. */
  inputId?: string,
  /** Disables entire field */
  isDisabled?: boolean,
  /**
   * Callback that will be used to determine if an item is disabled.
   * Defaults to the value of `item.isDisabled`.
   */
  isItemDisabled: (item: Item) => boolean,
  /** `true` if this select field is in a loading state */
  isLoading: boolean,
  /**
   * The list of items. Each item gets passed as props to `MenuItemComponent` to be rendered.
   */
  items: Array<Item>,
  /** id of the label that connects to this select field. */
  labelId?: string,
  /** a11y label that describes the options available inside the select menu, e.g. "List of items", "List of Countries", etc. */
  menuAriaLabel: string,
  /**
   * The Component to use for rendering each item in the list of `items`. Defaults to an internal implementation.
   */
  MenuItemComponent?: ComponentType<*>,

  name?: string,
  /** Callback that gets called when the user selects an item and the selected item has changed. */
  onChange: (item: *) => void,
  /** Placeholder to render inside the select toggle when no item is selected. */
  placeholder: string,
  /**
   * The Component to use for rendering the selected item.
   * WARNING: If you override this, you're also responsible for handling the cases where:
   * - `selectedItem` is empty
   */
  SelectionComponent?: ComponentType<*>,
  /**
   * Should `SearchableSelectField` complete and select based on
   * any text the user has entered into the text box on blur?
   */
  shouldSelectOnBlur: boolean,
};

const defaultFilterItems = (
  inputValue: string,
  items: Array<Item>,
  getLabelForItem: GetLabelForItem
) =>
  items.filter(item =>
    getLabelForItem(item)
      .toLowerCase()
      .startsWith(inputValue.toLowerCase())
  );

/**
 *
 * A replacement for a regular `<select>` field. Similar to SelectField, but with
 * search functionality.
 *
 * Features:
 * - Filtering of items by typing a search query in the select box (kind of like autocomplete)
 * - Allows customizing how the field looks like when there's something selected
 * - Allows customizing how the dropdown items render
 * - Keyboard support
 * - Completion of partial input to closest matching item on blur / TAB
 * - a11y friendly
 */

const SearchableSelectField = ({
  ariaLabel,
  autoComplete,
  autoFocus,
  filterItems,
  getKeyForItem,
  getLabelForItem,
  hasError,
  ignoredChanges,
  initialSelectedItem,
  isItemDisabled,
  inputId,
  isDisabled,
  isLoading,
  items,
  labelId,
  menuAriaLabel,
  MenuItemComponent,
  name,
  onChange,
  placeholder,
  SelectionComponent,
  shouldSelectOnBlur,
}: Props) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedItem, setSelectedItem] = useState(initialSelectedItem);
  const itemToString = (item?: Item) => (item ? getLabelForItem(item) : '');

  /**
   * Take over some of the state management from downshift.
   *
   * When there is a selected item and the user clicks on
   * the input, we want to clear out the input value, without
   * changing the selected item.
   */
  const onStateChange = (changes: StateChangeOptions<Item>) => {
    if (changes.hasOwnProperty('isOpen')) {
      setInputValue('');
    }
    if (changes.hasOwnProperty('inputValue')) {
      setInputValue(changes.inputValue);
    }
    if (changes.hasOwnProperty('selectedItem')) {
      if (
        ignoredChanges &&
        ignoredChanges.includes(getKeyForItem(changes.selectedItem))
      ) {
        setInputValue(itemToString(selectedItem));
        return;
      }

      setInputValue(itemToString(changes.selectedItem));
      setSelectedItem(changes.selectedItem);
    }
  };

  /**
   *
   * Take over some of the key handling from downshift.
   *
   * When the use presses 'Escape' and an item has already been selected,
   * we want to reset the input back to their current selection.
   */
  const onKeyDown = reset => event => {
    if (event.key === 'Escape' && selectedItem) {
      // Prevent Downshift's default 'Escape' behavior.
      event.nativeEvent.preventDownshiftDefault = true;
      reset({
        type: Downshift.stateChangeTypes.keyDownEscape,
        selectedItem,
        inputValue: itemToString(selectedItem),
      });
    }
  };

  /**
   * When the user blurs away (for example, hits TAB) from the input,
   * attempt to autocomplete and select the first matching item based
   * on the current input value.
   */
  const onBlurInput = (overrideValue: ?string) => {
    // Allow override value to be passed in (for autofill)
    const value =
      (typeof overrideValue === 'string' && overrideValue) || inputValue;

    // Don't search if no input value.
    if (!value) {
      return;
    }

    const matchingItems = filterItems(value, items, getLabelForItem);
    if (matchingItems.length === 0) {
      return;
    }

    const matchingItem = matchingItems[0];
    if (matchingItem !== selectedItem && !isItemDisabled(matchingItem)) {
      // never select a disabled item
      setInputValue(itemToString(matchingItem));
      setSelectedItem(matchingItem);
      onChange(matchingItem);
    }
  };

  return (
    <Downshift
      getLabelForItem={getLabelForItem}
      inputValue={inputValue}
      itemToString={itemToString}
      labelId={labelId}
      onChange={onChange}
      onStateChange={onStateChange}
      selectedItem={selectedItem}
    >
      {({
        getInputProps,
        getItemProps,
        getMenuProps,
        getRootProps,
        getToggleButtonProps,
        highlightedIndex,
        inputValue: downShiftInputValue,
        isOpen,
        reset,
        selectedItem: downshiftSelectedItem,
        toggleMenu,
      }) => (
        <div
          {...getRootProps(
            {
              'aria-label': ariaLabel,
              // Downshift will apply a generated labelId even when aria-label is supplied and no labelId.
              // I guess downshift always assumes there's a label? Anyway, if we do have an aria-label,
              // and do not have a labelId, force downshift to not add an arialabelled-by prop by
              // passing undefined in that case.
              'aria-labelledby': ariaLabel && !labelId ? undefined : labelId,
            },
            { suppressRefError: true }
          )}
          style={{ position: 'relative' }}
        >
          <SelectionBox
            autoComplete={autoComplete}
            autoFocus={autoFocus}
            getInputProps={overrideValue =>
              getInputProps({ onKeyDown: onKeyDown(reset), ...overrideValue })
            }
            getLabelForItem={getLabelForItem}
            getToggleButtonProps={getToggleButtonProps}
            hasError={hasError}
            inputId={inputId}
            inputValue={downShiftInputValue}
            isDisabled={isDisabled || !items.length}
            isLoading={isLoading}
            isOpen={isOpen}
            labelId={labelId}
            name={name}
            onBlurInput={overrideValue => {
              // only auto-complete when dropdown is open
              if (shouldSelectOnBlur && isOpen) {
                onBlurInput(overrideValue);
              }
            }}
            placeholder={placeholder}
            selectedItem={downshiftSelectedItem}
            SelectionComponent={SelectionComponent}
            toggleMenu={toggleMenu}
          />
          <Menu
            ariaLabel={menuAriaLabel}
            getItemProps={getItemProps}
            getKeyForItem={getKeyForItem}
            getLabelForItem={getLabelForItem}
            getMenuProps={getMenuProps}
            highlightedIndex={highlightedIndex}
            isItemDisabled={isItemDisabled}
            isOpen={isOpen}
            items={filterItems(downShiftInputValue, items, getLabelForItem)}
            MenuItemComponent={MenuItemComponent}
          />
        </div>
      )}
    </Downshift>
  );
};

SearchableSelectField.defaultProps = {
  filterItems: defaultFilterItems,
  getLabelForItem: (item: Item) => item.label,
  getKeyForItem: (item: Item) => item.value,
  hasError: false,
  // Initialize to null, not undefined, to avoid having downshift take over
  // the state. See https://github.com/paypal/downshift#control-props:
  // "As soon as this.props[statePropKey] !== undefined, internally,
  // downshift will determine its state based on your prop's value rather
  // than its own internal state."
  initialSelectedItem: null,
  isDisabled: false,
  isItemDisabled: (item: Item) => item.isDisabled,
  isLoading: false,
  shouldSelectOnBlur: true,
};

export default SearchableSelectField;
